import {F, getIntlTranslation, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import React, {useState} from 'react';
import {Button, Dropdown, Modal, Space, Spin} from 'antd';
import {useDispatch, useIntl} from 'umi';
import {CheckOutlined, DeleteOutlined, EditOutlined, MoreOutlined} from '@ant-design/icons';
import {renderButtonPreview} from '@/components/TaskAutomation/utils';
import {LeadTaskModel} from '@/typings/models/LeadTask';
import {hasPermissionToEditTask} from '@/utils/task';
import {runTaskUiConsequences} from '@/utils/taskUiConsequences';
import RetiredTaskDetails from '@/components/Lead/LeadTasks/RetiredTaskDetails';
import {doTaskActionRequest} from '@/services/api/taskAutomation';
import {partialEditLeadTaskRequest} from '@/services/api/leadTask';
import {useTaskSnooze} from '@/components/Tasks/TaskSnooze';
import {TaskAutomationButtonType} from '@/components/TaskAutomation/constants';

interface TaskActionsProps {
  task: LeadTaskModel;
  updateRequest: () => void;
  hideActionText?: boolean;
  size?: 'small' | 'middle' | 'large';
  layout?: 'horizontal' | 'vertical';
  allActionsInDropdown?: boolean;
  hideEdit?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  /** Task-automation preview: render the authored action buttons but fire
   * nothing, and drop the system items (done/snooze/edit/delete). */
  preview?: boolean;
}

// Helper function to get menu item styling based on ui_widget
const getMenuItemStyleFromWidget = (ui_widget: string | null) => {
  const style: React.CSSProperties = {};
  let danger = false;

  switch (ui_widget) {
    case TaskAutomationButtonType.Danger:
      danger = true;
      break;
    case TaskAutomationButtonType.Filled:
    case TaskAutomationButtonType.Solid:
      style.color = '#1890ff'; // primary color
      style.fontWeight = 500;
      break;
    case TaskAutomationButtonType.Blue:
      style.color = '#2f54eb'; // geekblue color
      style.fontWeight = 500;
      break;
    case TaskAutomationButtonType.Link:
      style.color = '#1890ff';
      break;
    case TaskAutomationButtonType.Text:
    case TaskAutomationButtonType.Normal:
    default:
      // Default styling
      break;
  }

  return { style, danger };
};

export const TaskActions: React.FC<TaskActionsProps> = ({
                                                          task,
                                                          updateRequest,
                                                          size = 'small',
                                                          layout = 'horizontal',
                                                          allActionsInDropdown = false,
                                                          hideEdit = false,
                                                          onLoadingChange,
                                                          preview = false,
                                                        }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const actions = [];
  // Preview shows the buttons regardless of task permissions; the handlers
  // below are all gated on `preview` so nothing actually runs.
  const canEdit = preview || hasPermissionToEditTask(task);

  // Helper to update loading state
  const updateLoading = (isLoading: boolean) => {
    setLoading(isLoading);
    onLoadingChange?.(isLoading);
  };

  const { snoozeMenuItem, snoozePopover } = useTaskSnooze({
    task,
    updateRequest,
    onLoadingChange: updateLoading,
  });

  if (task.retired_at) return allActionsInDropdown ? null : <RetiredTaskDetails task={task}/>;

  // Handle edit action
  // The details drawer is the single editing surface — title, description,
  // due date and assignees are all editable in place there
  const handleEdit = (task: LeadTaskModel) => {
    dispatch({
      type: 'leadTasks/openTaskDetailsDrawer',
      payload: { task },
    });
  };

  // Handle delete action
  const handleDelete = (taskId: string): Promise<void> => {
    return new Promise((resolve) => {
      dispatch({
        type: 'leadTasks/deleteTask',
        payload: {
          taskId,
          cb: () => {
            resolve();
            updateRequest();
          },
          intl,
        },
      });
    });
  };

  // Handle custom action
  const handleCustomAction = (action: any, task: LeadTaskModel) => {
    if (preview || task.is_completed) return;

    updateLoading(true);
    doTaskActionRequest({
      params: {
        taskId: task.id,
        actionId: action.uuid,
      },
    }).then(() => {
      updateLoading(false);
      updateRequest();
      // open_* consequences: the server no-ops them, the UI opens the surface
      // (falls back to a detail fetch when the list payload lacks consequences)
      runTaskUiConsequences(dispatch, task, action);
    }).catch(() => {
      updateLoading(false);
    });
  };

  // Handle mark as done action
  const handleMarkAsDone = (task: LeadTaskModel) => {
    if (!hasPermissionToEditTask(task)) return;

    updateLoading(true);
    // PATCH only the flag — a full PUT would clobber description with the
    // truncated description_summary that list payloads carry.
    partialEditLeadTaskRequest({
      params: {
        taskId: task.id,
      },
      data: {
        is_completed: true,
      },
    }).then(() => {
      updateLoading(false);
      updateRequest();
    }).catch(() => {
      updateLoading(false);
    });
  };

  // Create dropdown menu items
  const menuItems = [];

  // Add "Mark as Done" action if can_edit and not completed
  // (skipped in preview — these system items aren't part of the authored task)
  if (!preview && canEdit && !task.is_completed) {
    menuItems.push({
      key: 'mark-as-done',
      icon: <CheckOutlined />,
      label: <F id="pages.lead.tasks.do" defaultMessage="Mark as Done" />,
      onClick: () => handleMarkAsDone(task),
    });
    menuItems.push(snoozeMenuItem);
  }

  // Add custom actions to dropdown if allActionsInDropdown is true
  if (allActionsInDropdown && canEdit && task.actions && task.actions.length > 0) {
    if (menuItems.length > 0) {
      menuItems.push({ type: 'divider' });
    }

    task.actions.forEach((action) => {
      const { style, danger } = getMenuItemStyleFromWidget(action.ui_widget);
      menuItems.push({
        key: action.uuid,
        label: action.button_label,
        disabled: task.is_completed || !canEdit,
        onClick: () => handleCustomAction(action, task),
        style,
        danger,
      });
    });

    // Trailing divider only separates from the system items below, which the
    // preview drops — so it would dangle there.
    if (!preview) menuItems.push({ type: 'divider' });
  }

  // Add Edit action
  if (!preview && canEdit && !hideEdit) {
    menuItems.push({
      key: 'edit',
      icon: <EditOutlined />,
      label: <F id="common.actions.edit" defaultMessage="Edit" />,
      onClick: () => handleEdit(task),
    });
  }

  // Add Delete action
  if (!preview && canEdit) {
    menuItems.push({
      key: 'delete',
      icon: <DeleteOutlined />,
      label: <F id="common.actions.remove" defaultMessage="Delete" />,
      danger: true,
      onClick: () => {
        Modal.confirm({
          title: getIntlTranslation(intl, {
            entity: INTL_ENTITY.TASK,
            action: INTL_ACTION.REMOVE_CONFIRMATION
          }),
          onOk: () => handleDelete(task.id),
        });
      },
    });
  }

  // Add custom actions as separate buttons if allActionsInDropdown is false
  if (!allActionsInDropdown && canEdit && task.actions && task.actions.length > 0) {
    task.actions.forEach((action) => {
      actions.push(
        <div key={action.uuid}
             style={{ marginRight: layout === 'horizontal' ? 8 : 0, marginBottom: layout === 'vertical' ? 8 : 0 }}>
          {renderButtonPreview({
            ui_widget: action.ui_widget,
            button_label: action.button_label,
            disabled: task.is_completed || !canEdit,
            onClick: () => handleCustomAction(action, task),
          }, (key) => key)}
        </div>,
      );
    });
  }

  // Add three dot menu if there are menu items
  if (menuItems.length > 0) {
    actions.push(
      <Dropdown
        key="more-actions"
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={['click']}
      >
        <Button
          type="text"
          icon={<MoreOutlined />}
          size={size}
          aria-label={intl.formatMessage({ id: 'pages.tasks.moreActions', defaultMessage: 'More actions' })}
          onClick={(e) => e.preventDefault()}
        />
      </Dropdown>,
    );
  }

  // If no actions are available, don't render anything
  if (actions.length === 0) {
    return null;
  }

  if (layout === 'vertical') {
    return (
      <Spin spinning={loading}>
        <div onClick={e => e.stopPropagation()}
             style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {actions.filter(Boolean)}
          {snoozePopover}
        </div>
      </Spin>
    );
  }

  return (
    <Spin spinning={loading}>
      <Space size={8} onClick={e => e.stopPropagation()}>
        {actions.filter(Boolean)}
        {snoozePopover}
      </Space>
    </Spin>
  );
};

export default TaskActions;
